import SessionManager from '../utils/sessionManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';

// Device ID for session management
const getDeviceId = () => {
  let deviceId = localStorage.getItem('aptirix_device_id');
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('aptirix_device_id', deviceId);
  }
  return deviceId;
};

export async function request(endpoint, options = {}) {
  // Get token from current session using SessionManager
  let currentSession = SessionManager.getCurrentSession();
  
  // Fallback: if no current session, try to get candidate session directly
  if (!currentSession || !currentSession.access_token) {
    console.log('⚠️ [API] No current session, trying candidate session directly');
    currentSession = SessionManager.getSession('candidate');
  }
  
  const token = currentSession?.access_token;
  const deviceId = getDeviceId();
  
  console.log('🔐 [API] API Request:', {
    endpoint,
    method: options.method || 'GET',
    hasToken: !!token,
    tokenPreview: token ? `${token.substring(0, 30)}...` : 'NO TOKEN',
    deviceId,
    role: currentSession?.role
  });
  
  const headers = {
    'Content-Type': 'application/json',
    'X-Device-ID': deviceId,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  
  if (token) {
    console.log('✅ [API] Authorization header added:', `Bearer ${token.substring(0, 30)}...`);
  } else {
    console.warn('⚠️ [API] NO TOKEN - Request may fail if authentication required');
  }

  console.log('📤 [API] Request headers:', {
    'Content-Type': headers['Content-Type'],
    'X-Device-ID': headers['X-Device-ID'],
    'Authorization': headers['Authorization'] ? `Bearer ${headers['Authorization'].substring(7, 37)}...` : 'NOT SET'
  });

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  console.log('📥 [API] Response:', {
    endpoint,
    status: response.status,
    statusText: response.statusText,
    ok: response.ok
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      console.error('❌ [API] 401 Unauthorized - Token invalid or expired');
      console.error('❌ [API] Response detail:', data?.detail);
      console.error('❌ [API] Token that was sent:', token ? `${token.substring(0, 30)}...` : 'NO TOKEN');
      
      // Check if it's a device conflict
      if (data?.detail?.includes('logged in from another device') || data?.detail?.includes('device')) {
        alert('⚠️ You have been logged out because you logged in from another device.');
        SessionManager.clearAllSessions();
        window.location.href = '/login';
        throw new Error('Logged in from another device');
      }
      
      // Clear session and redirect to login
      console.log('🔄 [API] Clearing session and redirecting to login');
      SessionManager.clearAllSessions();
      
      // Don't redirect if we're already on login page
      if (!window.location.pathname.includes('/login')) {
        alert('⚠️ Your session has expired. Please log in again.');
        window.location.href = '/login';
      }
      
      throw new Error('Session expired. Please log in again.');
    }
    
    // Handle validation errors (422) with more detail
    let message = data?.detail || 'Something went wrong. Please try again.';
    
    // If it's a validation error, try to extract more details
    if (response.status === 422 && data?.errors) {
      const errorMessages = data.errors.map(err => {
        const field = err.loc?.join('.') || 'field';
        return `${field}: ${err.msg}`;
      });
      message = errorMessages.join('; ') || message;
    } else if (response.status === 422 && typeof data?.detail === 'string') {
      message = data.detail;
    }
    
    console.error('❌ [API] Error:', {
      endpoint,
      status: response.status,
      message,
      detail: data?.detail
    });
    
    throw new Error(message);
  }

  console.log('✅ [API] Request successful');
  return data;
}

function buildQueryString(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      qs.set(key, value);
    }
  });
  const query = qs.toString();
  return query ? `?${query}` : '';
}

export function login({ email, password, role, preferredCourses }) {
  const deviceId = getDeviceId();
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      email,
      password,
      role,
      preferred_courses: preferredCourses,
      device_id: deviceId,
    }),
  });
}

export function checkUserExists(email) {
  return request('/auth/check-user-exists', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function signupCandidate(payload) {
  return request('/auth/signup/candidate', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function signupHr(payload) {
  return request('/auth/signup/hr', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// OTP Functions for Registration
export function sendOtp({ email }) {
  return request('/auth/send-otp', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function verifyOtp({ email, otp }) {
  return request('/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  });
}

// Password Reset Functions
export function forgotPassword({ email, userType }) {
  return request('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({
      email,
      user_type: userType,
    }),
  });
}

export function verifyResetOTP({ email, otp, userType }) {
  return request('/auth/verify-reset-otp', {
    method: 'POST',
    body: JSON.stringify({
      email,
      otp,
      user_type: userType,
    }),
  });
}

export function resetPassword({ email, otp, newPassword, confirmPassword, userType }) {
  return request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({
      email,
      otp,
      new_password: newPassword,
      confirm_password: confirmPassword,
      user_type: userType,
    }),
  });
}

export function loginSuperAdmin({ username, password }) {
  return request('/super-admin/login', {
    method: 'POST',
    body: JSON.stringify({
      username,
      password,
    }),
  });
}

export function loginCourseProvider({ username, password }) {
  return request('/course-provider/login', {
    method: 'POST',
    body: JSON.stringify({
      username,
      password,
    }),
  });
}

export function loginSaaSProvider({ username, password }) {
  return request('/saas-provider/login', {
    method: 'POST',
    body: JSON.stringify({
      username,
      password,
    }),
  });
}

export function getJobs(params = {}) {
  const query = buildQueryString(params);
  return request(`/jobs${query}`);
}

export function createJob(payload) {
  return request('/jobs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getJobDetails(jobId) {
  return request(`/jobs/${jobId}`);
}

export function applyToJob(jobId, payload) {
  return request(`/jobs/${jobId}/apply`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getApplications(params = {}) {
  const query = buildQueryString(params);
  return request(`/applications${query}`);
}

export function deleteApplication(applicationId) {
  return request(`/applications/${applicationId}`, {
    method: 'DELETE',
  });
}

export function updateApplicationStatus(applicationId, status) {
  return request(`/applications/${applicationId}/status?new_status=${status}`, {
    method: 'PATCH',
  });
}

export function updateInterviewDetails(applicationId, payload) {
  return request(`/applications/${applicationId}/interview`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function updateJobStatus(jobId, status) {
  return request(`/jobs/${jobId}/status?status=${status}`, {
    method: 'PATCH',
  });
}

export function updateJob(jobId, payload) {
  return request(`/jobs/${jobId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function deleteJob(jobId) {
  return request(`/jobs/${jobId}`, {
    method: 'DELETE',
  });
}

export function getCourses() {
  return request('/courses');
}

export function purchaseCourse(courseId, candidateId) {
  const query = buildQueryString({ candidate_id: candidateId });
  return request(`/courses/${courseId}/purchase${query}`, {
    method: 'POST',
  });
}

export function scoreResume(payload) {
  return request('/ats/score', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getCandidateProfile(candidateId, email, manualInputParams = {}) {
  const params = new URLSearchParams();
  if (candidateId) params.set('candidate_id', candidateId);
  if (email) params.set('email', email);
  
  // Add manual input parameters if provided
  if (manualInputParams.manual_name) params.set('manual_name', manualInputParams.manual_name);
  if (manualInputParams.manual_email) params.set('manual_email', manualInputParams.manual_email);
  if (manualInputParams.manual_role) params.set('manual_role', manualInputParams.manual_role);
  if (manualInputParams.manual_skills) params.set('manual_skills', manualInputParams.manual_skills);
  
  // Add cache-busting parameter
  if (manualInputParams._t) params.set('_t', manualInputParams._t);
  
  const query = params.toString();
  return request(`/candidates/profile${query ? `?${query}` : ''}`);
}

export function updateCandidateProfile(candidateId, formData) {
  // Create FormData for file upload
  const data = new FormData();
  
  // Only append candidate_id if it's a valid candidate ID
  // If not provided, backend will look up by email
  if (candidateId && candidateId.trim() !== '') {
    data.append('candidate_id', candidateId);
  }
  
  data.append('full_name', formData.fullName || '');
  data.append('email', formData.email || '');
  data.append('phone', formData.phone || '');
  data.append('job_role', formData.jobRole || '');
  data.append('skills', JSON.stringify(formData.skills || []));
  
  if (formData.resume) {
    data.append('resume', formData.resume);
  }

  // Get token from current session using SessionManager
  const currentSession = SessionManager.getCurrentSession();
  const token = currentSession?.access_token;
  
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  console.log('Sending profile update request:', {
    url: `${API_BASE_URL}/candidates/profile`,
    hasResume: !!formData.resume,
    resumeName: formData.resume?.name,
  });

  return fetch(`${API_BASE_URL}/candidates/profile`, {
    method: 'PUT',
    headers,
    body: data,
  }).then(async (response) => {
    const responseData = await response.json().catch(() => ({}));
    console.log('Profile update response:', {
      status: response.status,
      ok: response.ok,
      data: responseData,
    });
    
    if (!response.ok) {
      const message = responseData?.detail || 'Something went wrong. Please try again.';
      console.error('Profile update failed:', message);
      throw new Error(message);
    }
    return responseData;
  }).catch((error) => {
    console.error('Profile update error:', error);
    throw error;
  });
}

export function analyzeMatch(applicationId) {
  return request('/match-analysis/analyze', {
    method: 'POST',
    body: JSON.stringify({ application_id: applicationId }),
  });
}

export function enhanceResume(applicationId) {
  return request('/match-analysis/enhance-resume', {
    method: 'POST',
    body: JSON.stringify({ application_id: applicationId }),
  });
}


// Interview APIs
export function generateInterviewQuestions(applicationId, numQuestions = 5) {
  return request('/interview/generate-questions', {
    method: 'POST',
    body: JSON.stringify({
      application_id: applicationId,
      num_questions: numQuestions,
    }),
  });
}

export function saveInterviewAnswer(payload) {
  return request('/interview/save-answer', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function logProctoringEvent(payload) {
  return request('/interview/log-proctoring-event', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function completeInterview(interviewId, status = 'completed', terminationReason = null) {
  return request('/interview/complete', {
    method: 'POST',
    body: JSON.stringify({
      interview_id: interviewId,
      status,
      termination_reason: terminationReason,
    }),
  });
}

// AI Trainer APIs
export function startTrainingSession(payload) {
  return request('/ai-trainer/start-session', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function submitTrainingAnswer(payload) {
  return request('/ai-trainer/submit-answer', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function completeTrainingSession(payload) {
  return request('/ai-trainer/complete-session', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getTrainingSessionStatus(sessionId) {
  return request('/ai-trainer/session-status', {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId }),
  });
}

export function getTrainingSessionSummary(sessionId) {
  return request(`/ai-trainer/session/${sessionId}/summary`);
}

export function getCandidateTrainingSessions(candidateId) {
  return request(`/ai-trainer/candidate-sessions/${candidateId}`);
}

// NEW: Get trainer reports for candidate dashboard
export function getCandidateTrainerReports(candidateId) {
  return request(`/candidates/${candidateId}/trainer-reports`);
}

export function getCandidateTrainerSummary(candidateId) {
  return request(`/candidates/${candidateId}/trainer-summary`);
}

export function getLiveInterviewSessions(candidateId = null, candidateEmail = null) {
  const params = new URLSearchParams();
  if (candidateId) params.set('candidate_id', candidateId);
  if (candidateEmail) params.set('candidate_email', candidateEmail);
  const queryString = params.toString();
  return request(`/live-interview/candidate-sessions${queryString ? `?${queryString}` : ''}`);
}

export function parseResumeWithAI(resumeFile) {
  const formData = new FormData();
  formData.append('resume', resumeFile);
  
  const currentSession = SessionManager.getCurrentSession();
  const token = currentSession?.access_token;
  
  return fetch(`${API_BASE_URL}/candidates/parse-resume-ai`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: formData
  }).then(async (response) => {
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.detail || 'Failed to parse resume');
    }
    return data;
  });
}

export function parseResumeFile(file) {
  const formData = new FormData();
  formData.append('resume', file);
  
  const currentSession = SessionManager.getCurrentSession();
  const token = currentSession?.access_token;
  
  return fetch(`${API_BASE_URL}/candidates/parse-resume`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  }).then(async (response) => {
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.detail || 'Failed to parse resume');
    }
    return data;
  });
}


// ============================================
// CREDIT SYSTEM APIs
// ============================================

export function getCreditBalance(candidateId) {
  return request(`/credits/balance?candidate_id=${candidateId}`);
}

export function claimCreditsWithPin(candidateId, pin) {
  return request(`/credits/claim-pin?candidate_id=${candidateId}`, {
    method: 'POST',
    body: JSON.stringify({ pin }),
  });
}

export function submitCreditRequest(candidateId, requestedAmount, reason) {
  return request(`/credits/request?candidate_id=${candidateId}`, {
    method: 'POST',
    body: JSON.stringify({
      requested_amount: requestedAmount,
      reason,
    }),
  });
}

export function getCreditRequestStatus(candidateId) {
  return request(`/credits/request/status?candidate_id=${candidateId}`);
}

export function getCreditTransactions(candidateId, limit = 50) {
  return request(`/credits/transactions?candidate_id=${candidateId}&limit=${limit}`);
}

export function checkAndGrantCredits(candidateId) {
  return request(`/credits/check-and-grant?candidate_id=${candidateId}`, {
    method: 'POST',
  });
}

export function getServiceCosts() {
  return request('/credits/service-costs');
}

export function deductCredits(candidateId, serviceType, referenceId = null) {
  let url = `/credits/deduct?candidate_id=${candidateId}&service_type=${serviceType}`;
  if (referenceId) {
    url += `&reference_id=${referenceId}`;
  }
  return request(url, {
    method: 'POST',
  });
}

// ============================================
// SUPER ADMIN CREDIT APIs
// ============================================

export function getAdminCreditRequests(statusFilter = null, limit = 100) {
  let url = `/super-admin/credits/requests?limit=${limit}`;
  if (statusFilter) {
    url += `&status_filter=${statusFilter}`;
  }
  return request(url);
}

export function approveCreditsRequest(requestId, approvedAmount, adminNotes, adminId) {
  return request(`/super-admin/credits/requests/${requestId}/approve?admin_id=${adminId}`, {
    method: 'POST',
    body: JSON.stringify({
      approved_amount: approvedAmount,
      admin_notes: adminNotes,
    }),
  });
}

export function rejectCreditsRequest(requestId, adminNotes, adminId) {
  return request(`/super-admin/credits/requests/${requestId}/reject?admin_id=${adminId}`, {
    method: 'POST',
    body: JSON.stringify({
      admin_notes: adminNotes,
    }),
  });
}

export function getCreditSettings() {
  return request('/super-admin/credits/settings');
}

export function updateCreditSettings(settings, adminId) {
  return request(`/super-admin/credits/settings?admin_id=${adminId}`, {
    method: 'PUT',
    body: JSON.stringify({ settings }),
  });
}

export function getCreditSettingsHistory(settingKey = null, limit = 50) {
  let url = `/super-admin/credits/settings/history?limit=${limit}`;
  if (settingKey) {
    url += `&setting_key=${settingKey}`;
  }
  return request(url);
}

export function getCreditDashboard(period = 'all') {
  return request(`/super-admin/credits/dashboard?period=${period}`);
}

export function getAllCreditTransactions(candidateId = null, transactionType = null, serviceType = null, limit = 100) {
  let url = `/super-admin/credits/transactions?limit=${limit}`;
  if (candidateId) url += `&candidate_id=${candidateId}`;
  if (transactionType) url += `&transaction_type=${transactionType}`;
  if (serviceType) url += `&service_type=${serviceType}`;
  return request(url);
}

export function getUserCreditDetails(candidateId) {
  return request(`/super-admin/credits/user/${candidateId}`);
}

export function getAllUsersCredits(search = null, limit = 100) {
  let url = `/super-admin/credits/users?limit=${limit}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  return request(url);
}

export function grantCreditsManually(candidateId, amount, reason, adminId) {
  return request(`/super-admin/credits/grant-manual?candidate_id=${candidateId}&amount=${amount}&reason=${encodeURIComponent(reason)}&admin_id=${adminId}`, {
    method: 'POST',
  });
}
// ============================================
// Document Verification API Functions
// ============================================

export async function requestDocumentVerification(applicationId, candidateData) {
  return request('/document-verification/request', {
    method: 'POST',
    body: JSON.stringify({
      candidate_id: candidateData.candidate_id,
      application_id: applicationId,
      candidate_name: candidateData.candidate_name,
      candidate_email: candidateData.candidate_email,
      required_documents: candidateData.required_documents || null
    })
  });
}

export async function getDocumentVerificationRequests(status = null) {
  const params = status ? `?status=${status}` : '';
  return request(`/document-verification/requests${params}`);
}

export async function getDocumentVerificationDocuments(requestId) {
  return request(`/document-verification/request/${requestId}/documents`);
}

export async function verifyDocument(documentId, status, notes) {
  const formData = new FormData();
  formData.append('status', status);
  formData.append('notes', notes);
  // Removed verified_by parameter - backend now gets this from JWT token

  // Get current session for auth
  const currentSession = SessionManager.getCurrentSession();
  const authToken = currentSession?.access_token;
  const deviceId = getDeviceId();
  
  const headers = {
    'X-Device-ID': deviceId,
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    // Don't set Content-Type - let browser set it for FormData
  };

  console.log('📤 [API] Document verification request:', {
    endpoint: `/document-verification/document/${documentId}/verify`,
    documentId,
    status,
    notes
  });

  const response = await fetch(`${API_BASE_URL}/document-verification/document/${documentId}/verify`, {
    method: 'POST',
    headers,
    body: formData
  });

  console.log('📥 [API] Document verification response:', {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    let message = data?.detail || 'Verification failed. Please try again.';
    
    // Handle validation errors (422) with more detail
    if (response.status === 422 && data?.errors) {
      const errorMessages = data.errors.map(err => {
        const field = err.loc?.join('.') || 'field';
        return `${field}: ${err.msg}`;
      });
      message = errorMessages.join('; ') || message;
    }
    
    console.error('❌ [API] Document verification error:', {
      status: response.status,
      message,
      detail: data?.detail
    });
    
    throw new Error(message);
  }

  console.log('✅ [API] Document verification successful');
  return data;
}

export async function uploadDocument(token, documentType, file) {
  const formData = new FormData();
  formData.append('token', token);
  formData.append('document_type', documentType);
  formData.append('file', file);

  // Get current session for auth (though this endpoint might not need it)
  const currentSession = SessionManager.getCurrentSession();
  const authToken = currentSession?.access_token;
  const deviceId = getDeviceId();
  
  const headers = {
    'X-Device-ID': deviceId,
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    // Don't set Content-Type - let browser set it for FormData with boundary
  };

  console.log('📤 [API] Document upload request:', {
    endpoint: '/document-verification/upload',
    token: token ? `${token.substring(0, 20)}...` : 'NO TOKEN',
    documentType,
    fileName: file?.name,
    fileSize: file?.size
  });

  const response = await fetch(`${API_BASE_URL}/document-verification/upload`, {
    method: 'POST',
    headers,
    body: formData
  });

  console.log('📥 [API] Document upload response:', {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    let message = data?.detail || 'Upload failed. Please try again.';
    
    // Handle validation errors (422) with more detail
    if (response.status === 422 && data?.errors) {
      const errorMessages = data.errors.map(err => {
        const field = err.loc?.join('.') || 'field';
        return `${field}: ${err.msg}`;
      });
      message = errorMessages.join('; ') || message;
    }
    
    console.error('❌ [API] Document upload error:', {
      status: response.status,
      message,
      detail: data?.detail
    });
    
    throw new Error(message);
  }

  console.log('✅ [API] Document upload successful');
  return data;
}

export async function getUploadStatus(token) {
  return request(`/document-verification/upload-status/${token}`);
}

export async function viewDocument(documentId) {
  return request(`/document-verification/document/${documentId}/view`);
}

export async function submitDocumentVerification(token) {
  return request('/document-verification/submit', {
    method: 'POST',
    body: JSON.stringify({ token })
  });
}
